import React, { FC, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import SimpleEditorHeader from '@/editor/lib/components/EditorHeader/SimpleEditorHeader';
import { formatAPITimestamp } from '@/utils/dates';
import {
  TextField,
  Checkbox,
  Autocomplete,
  Button,
  FormGroup,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import Container from '../../components/Container';
import { Api } from '../../services/Api';
import { useApi } from '../../hooks/useApi';
import Styles from '../../assets/js/Styles';
import { isDeepEqual } from '../../utils/object';
import { Site } from '../../declarations/models/Site';
import { Domain } from '../../declarations/models/Domain';
import { Owner } from '../../declarations/models/Owner';
import { Status } from '../../declarations/models/Status';

export interface SiteEditorProps {
  siteId?: number | null;
  ownerId?: number | null;
  ownerName?: string | null;
  onCloseEditor: () => void;
}

export const editorStyling = {
  maxHeight: '100%',
  overflowY: 'auto',
  padding: 3,
  borderRadius: 2,
  backgroundColor: Styles.Colors.FINDER_M24_PRIMARY_HEADER_BACKGROUND,
};

export type SiteTextFields = 'name' | 'owner_id' | 'description' | 'default_language';
export type DomainTextFields = 'name' | 'path';

export const SiteEditor: FC<SiteEditorProps> = ({ siteId, ownerId, ownerName, onCloseEditor }) => {
  const emptySite = {
    name: '',
    owner_id: ownerId || undefined,
    description: '',
    default_language: 'no',
    is_admin2_0_ready: true,
    status: Status.DRAFT,
    content: {
      settings: {
        disableBaseCss: true,
        m24version: 2,
        siteVariant: 'standard',
        v2: true,
        v2css: 'current',
      },
    },
  };

  const { t: tCommon } = useTranslation('common');
  const { t: tComp } = useTranslation('components');
  const { enqueueSnackbar } = useSnackbar();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDomainDirty, setIsDomainDirty] = useState<boolean>(false);
  const [defaultLanguageCreated, setDefaultLanguageCreated] = useState<boolean>(false);
  const [savedSite, setSavedSite] = useState<Partial<Site>>();
  const [modifiedSite, setModifiedSite] = useState<Partial<Site>>();
  const [savedDomain, setSavedDomain] = useState<Partial<Domain>>();
  const [modifiedDomain, setModifiedDomain] = useState<Partial<Domain>>();
  const [availableOwners, isLoadingAvailableOwners] = useApi(() => Api.getOwners({ rows: 500 }), []);
  const [refreshDep, setRefreshDep] = useState(Date.now);
  const [newSiteId, setNewSiteId] = useState<number | null>();
  const refreshPage = () => setRefreshDep(Date.now());
  const [languages, isLoadingLanguages, reloadLanguages] = useApi(() => Api.getLanguages(false), []);
  const availableLanguageCodes = languages.map((l) => {
    return l.code;
  });

  const getEmptyLanguageCreationModel = useCallback(
    (languageCode?: string) => {
      return {
        site_id: siteId || newSiteId,
        status: Status.DRAFT,
        is_admin2_0_ready: true,
        content: {
          sections: [
            {
              type: 'site__head',
              blocks: [
                {
                  type: 'menu',
                  required: true,
                  multiple: false,
                  items: [],
                  searchresultPage: {},
                  parentOverlayPage: {},
                  focusitems: [],
                },
              ],
              _collapse: true,
            },
            {
              type: 'site__footer',
              blocks: [
                {
                  type: 'footer__address',
                  required: true,
                  multiple: false,
                  addresses: [],
                },
                {
                  type: 'footer__social',
                  required: false,
                  multiple: false,
                },
                {
                  type: 'footer__info',
                  required: false,
                  multiple: false,
                  mediaobjects: [],
                },
                {
                  type: 'footer__menu',
                  required: false,
                  multiple: false,
                },
              ],
              _collapse: true,
            },
          ],
          settings: {},
        },
        locale: languageCode || 'no',
      } as unknown as Site;
    },
    [siteId, newSiteId],
  );

  const getEmptyDomainModel = useCallback(() => {
    return {
      name: '',
      path: '',
      site_id: siteId || newSiteId,
      status: 'general',
    } as unknown as Domain;
  }, [siteId, newSiteId]);

  useEffect(() => {
    reloadLanguages();
  }, [reloadLanguages]);
  const changeOwner = (parent: Owner | null) => {
    if (!parent) {
      return;
    }
    setModifiedSite({
      ...modifiedSite,
      owner_id: parent.id,
    });
  };

  useEffect(() => {
    // console.log('modifiedOwner', modifiedOwner);
  }, [modifiedSite]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (siteId || newSiteId) {
      setIsLoading(true);
      const ctx = Api.getSite(newSiteId || siteId || 0);
      ctx
        .fetchDirect(null)
        .catch((e) => console.error(e))
        .then((res) => {
          if (res) {
            setIsLoading(false);
            setSavedSite(res);
          }
        })
        .finally(ctx.abort);

      const ctx2 = Api.getDomainForSite(newSiteId || siteId || 0);
      ctx2
        .fetchDirect(null)
        .catch((e) => console.error(e))
        .then((res) => {
          if (res) {
            // console.log('domain', res);
            setSavedDomain(res);
          } else {
            setSavedDomain(getEmptyDomainModel());
            // console.log('domain empty', savedDomain);
          }
        })
        .finally(ctx2.abort);
    } else {
      setSavedSite(emptySite);
    }
  }, [siteId, newSiteId, refreshDep]);
  /* eslint-enable */

  useEffect(() => {
    setModifiedSite(savedSite);
  }, [savedSite]);

  useEffect(() => setIsDirty(!isDeepEqual(savedSite, modifiedSite)), [savedSite, modifiedSite]);
  useEffect(() => setIsDomainDirty(!isDeepEqual(savedDomain, modifiedDomain)), [savedDomain, modifiedDomain]);

  const handleSaveSite = async () => {
    const empty_required = [];
    if (!modifiedSite?.name) empty_required.push('name');
    if (!modifiedSite?.owner_id) empty_required.push('owner_id');
    if (!modifiedSite?.default_language) empty_required.push('default_language');
    if (empty_required.length) {
      enqueueSnackbar(tComp('EmployeeEditor.RequiredFieldsMissing', { fields: empty_required.join(', ') }), {
        variant: 'error',
      });
    } else if (modifiedSite) {
      // const id = savedOwner?.id;
      // console.log('Save modifiedOwner', modifiedOwner);
      const ctx = Api.saveSiteSettings(modifiedSite);
      await ctx
        .fetchDirect(null)
        .then(async (res) => {
          if (res) {
            refreshPage();
            setNewSiteId(res.id);
            // console.log('savedOwner', savedOwner);
            enqueueSnackbar('Oppdatering vellykket', { variant: 'success' });

            if (!siteId && !defaultLanguageCreated) {
              // alert('Create site content');
              // console.log('Create site content for site-id', savedSite?.id);
              // console.log('Create site content for language-code', modifiedSite?.default_language);
              await Api.saveSiteContent<Site>(
                getEmptyLanguageCreationModel(modifiedSite?.default_language),
                res?.id || 0,
                modifiedSite?.default_language || 'no',
              )
                .fetchDirect(null)
                .then((res2) => {
                  if (res2) {
                    // console.log('defaultLanguage', defaultLanguage);
                    setDefaultLanguageCreated(true);
                  }
                })
                .catch((e) =>
                  enqueueSnackbar(tComp('EmployeeEditor.SaveFailed', { error: e.error || e }), { variant: 'error' }),
                );
            }
          }
        })
        .catch((e) =>
          enqueueSnackbar(tComp('EmployeeEditor.SaveFailed', { error: e.error || e }), { variant: 'error' }),
        );
    }
  };

  const handleChangeStringValue = (attribute: SiteTextFields, value: string) => {
    setModifiedSite({
      ...modifiedSite,
      [attribute]: value,
    });
  };

  const handleChangeDomainStringValue = (attribute: DomainTextFields, value: string) => {
    setModifiedDomain({
      ...modifiedDomain,
      [attribute]: value,
    });
  };

  const handleSaveDomain = async () => {
    if (modifiedDomain) {
      const ctx = Api.saveDomainForSite(modifiedDomain, newSiteId || siteId || 0);
      await ctx
        .fetchDirect(null)
        .then(async (res) => {
          if (res) {
            refreshPage();
            enqueueSnackbar('Oppdatering vellykket', { variant: 'success' });
          }
        })
        .catch((e) =>
          enqueueSnackbar(tComp('EmployeeEditor.SaveFailed', { error: e.error || e }), { variant: 'error' }),
        );
    }
  };

  useEffect(() => {
    setModifiedDomain(savedDomain);
  }, [savedDomain]);

  return (
    <>
      {!isLoading && (
        <Container fullHeight fullWidth column gap={3}>
          <SimpleEditorHeader
            model={savedSite}
            title={
              savedSite?.id
                ? `Nettsted: ${savedSite.name}  / Eier: ${savedSite?.owner?.name}`
                : `Nytt nettsted under ${ownerName || '...'}`
            }
            onClose={onCloseEditor}
            onSave={handleSaveSite}
            isDirty={isDirty}
          />
          <Container fullWidth gap={2} sx={{ marginTop: '16px', height: 'calc(100% -  56px)' }}>
            <Container fullWidth fullHeight left column top gap={1} sx={editorStyling}>
              {savedSite?.id && (
                <div>
                  <p>
                    <strong style={{ fontWeight: 'bold', fontSize: '24px' }}>{savedSite.name}</strong> |{' '}
                    <a
                      target='_blank'
                      rel='noopener noreferrer'
                      href={`//${savedSite?.url?.replace('https://', '//')}`}>
                      {savedSite?.url}
                    </a>
                  </p>
                  <p>
                    Nettsteds ID: {savedSite?.id} - Opprettet: {formatAPITimestamp(savedSite?.created_at)} | Sist
                    endret: {formatAPITimestamp(savedSite?.updated_at)} <br />
                  </p>
                </div>
              )}

              <Container fullWidth top gap={4} mt={4} mb={2}>
                <TextField
                  size='small'
                  label='Navn'
                  sx={{ width: '25%' }}
                  fullWidth
                  required
                  value={modifiedSite?.name ?? ''}
                  onChange={(e) => handleChangeStringValue('name', e.target.value)}
                />
                <TextField
                  multiline
                  size='small'
                  label='Description'
                  fullWidth
                  required
                  value={modifiedSite?.description ?? ''}
                  onChange={(e) => handleChangeStringValue('description', e.target.value)}
                />

                <Autocomplete
                  value={modifiedSite?.status || ''}
                  getOptionLabel={(s) => tCommon(`Status.${s}`)}
                  options={Object.values(Status)}
                  onChange={(_, value: string | null) => {
                    setModifiedSite({
                      ...modifiedSite,
                      status: (value as Status) || Status.PUBLISHED,
                    });
                  }}
                  sx={{ width: '33%' }}
                  renderInput={(params) => <TextField {...params} label='Status' />}
                />
              </Container>
              <Container fullWidth top gap={4} left mb={2}>
                <TextField
                  sx={{ width: '25%' }}
                  size='small'
                  label='Eier ID'
                  fullWidth
                  required
                  disabled
                  value={modifiedSite?.owner_id ?? ''}
                  onChange={(e) => handleChangeStringValue('owner_id', e.target.value)}
                />

                {savedSite?.id || !ownerName ? (
                  <Autocomplete
                    value={null}
                    getOptionLabel={(option: Owner) => option.name}
                    loading={isLoadingAvailableOwners}
                    loadingText={tCommon('loading')}
                    options={Array.isArray(availableOwners) ? [] : availableOwners?.owners ?? []}
                    onChange={(_, newValue: Owner | null) => {
                      changeOwner(newValue);
                    }}
                    sx={{ width: '25%' }}
                    renderInput={(params) => <TextField {...params} label='Velg eller endre eier' />}
                    slotProps={{
                      listbox: {
                        style: {
                          maxHeight: 230,
                        },
                      },
                    }}
                  />
                ) : (
                  <span style={{ width: '25%' }} />
                )}

                <TextField
                  size='small'
                  label='Hovedspråk'
                  sx={{ width: '25%' }}
                  fullWidth
                  required
                  disabled
                  value={modifiedSite?.default_language ?? ''}
                  onChange={(e) => handleChangeStringValue('default_language', e.target.value)}
                />

                <Autocomplete
                  value={modifiedSite?.default_language ?? ''}
                  getOptionLabel={(l) => (l ? tCommon(`Languages.${l}`) : tCommon(`none`))}
                  loading={isLoadingLanguages}
                  loadingText={tCommon('loading')}
                  options={['', ...availableLanguageCodes]}
                  onChange={(_, newValue: string | null | undefined) => {
                    handleChangeStringValue('default_language', newValue || '');
                  }}
                  sx={{ width: '25%' }}
                  renderInput={(params) => <TextField {...params} label='Velg eller endre standard språk' />}
                  slotProps={{
                    listbox: {
                      style: {
                        maxHeight: 230,
                      },
                    },
                  }}
                />
              </Container>
              <Container fullWidth top gap={4} left mt={2}>
                <FormGroup sx={{ width: '25%' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='medium'
                        checked={modifiedSite?.is_admin2_0_ready ?? false}
                        onChange={(e) => {
                          setModifiedSite({
                            ...modifiedSite,
                            is_admin2_0_ready: e.target.checked,
                          });
                        }}
                      />
                    }
                    label='Tilgjengelig i Museum24 2.0 **'
                  />
                </FormGroup>

                <FormControlLabel
                  sx={{ width: '25%' }}
                  control={
                    <Checkbox
                      size='medium'
                      checked={modifiedSite?.content?.settings?.disableBaseCss ?? false}
                      onChange={(e) => {
                        setModifiedSite({
                          ...modifiedSite,
                          content: {
                            ...modifiedSite?.content,
                            settings: {
                              ...modifiedSite?.content?.settings,
                              disableBaseCss: e.target.checked,
                            },
                          },
                        });
                      }}
                    />
                  }
                  label='Bruk Museum24 2.0 CSS'
                />

                {modifiedSite?.content?.settings?.disableBaseCss && (
                  <FormControl sx={{ width: '25%' }}>
                    <InputLabel id='site-variant-label'>Menytype</InputLabel>
                    <Select
                      value={modifiedSite?.content?.settings?.siteVariant ?? ''}
                      onChange={(e) => {
                        setModifiedSite({
                          ...modifiedSite,
                          content: {
                            ...modifiedSite?.content,
                            settings: {
                              ...modifiedSite?.content?.settings,
                              siteVariant: e.target.value,
                            },
                          },
                        });
                      }}>
                      <MenuItem value='standard'>Standardmeny</MenuItem>
                      <MenuItem value='focus'>Fokusmeny</MenuItem>
                    </Select>
                  </FormControl>
                )}
                {modifiedSite?.content?.settings?.disableBaseCss &&
                  modifiedSite?.content?.settings?.siteVariant === 'standard' && (
                    <FormControl sx={{ width: '25%' }}>
                      <InputLabel id='site-variant-label'>Desktop Brekkpunkt</InputLabel>

                      <Select
                        value={modifiedSite?.content?.settings?.desktopBreakpoint || '1024'}
                        onChange={(e) => {
                          setModifiedSite({
                            ...modifiedSite,
                            content: {
                              ...modifiedSite?.content,
                              settings: {
                                ...modifiedSite?.content?.settings,
                                desktopBreakpoint: e.target.value,
                              },
                            },
                          });
                        }}>
                        <MenuItem value='1024'>1024px</MenuItem>
                        <MenuItem value='1280'>1280px</MenuItem>
                      </Select>
                    </FormControl>
                  )}
              </Container>
            </Container>
          </Container>

          {savedSite?.id && (
            <Container fullWidth fullHeight left column mt={4} top gap={2} sx={editorStyling}>
              <strong>URL / DOMENE:</strong>
              <Container fullWidth top gap={2}>
                <TextField
                  size='small'
                  label='Navn / url'
                  fullWidth
                  required
                  value={modifiedDomain?.name ?? ''}
                  onChange={(e) => handleChangeDomainStringValue('name', e.target.value)}
                />
                <TextField
                  size='small'
                  label='Path'
                  fullWidth
                  required
                  value={modifiedDomain?.path ?? ''}
                  onChange={(e) => handleChangeDomainStringValue('path', e.target.value)}
                />

                <Button
                  variant='contained'
                  color='secondary'
                  fullWidth
                  disabled={!isDomainDirty}
                  onClick={handleSaveDomain}>
                  Lagre
                </Button>
              </Container>
            </Container>
          )}
        </Container>
      )}
    </>
  );
};

export default SiteEditor;
